import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase/config';
import { 
  loadVehicles, 
  saveVehicles, 
  getInvoices, 
  getAllInvoices,
  saveInvoice,
  generateInvoiceNumber,
  createInvoiceForVehicle as createInvoiceForVehicleFunc,
  loadGarageContact,
  saveGarageContact,
  shouldUseFirestore,
  subscribeInvoices,
  subscribeVehicles,
  subscribeGarageInfo,
  DEFAULT_GARAGE_INFO
} from '../utils/dataUtils';
import { garageInfoService } from '../services/firestoreService';
import { applyPlatformBranding, normalizeBrandingPayload } from '../utils/brandingSync';
import { normalizeEnabledStages } from '../utils/enabledStages';

const AppContext = createContext();

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};

export const AppProvider = ({ children, vehiclesCountRef }) => {
  const [vehicles, setVehicles] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [garageInfo, setGarageInfo] = useState(DEFAULT_GARAGE_INFO);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [authUserId, setAuthUserId] = useState(() => auth.currentUser?.uid ?? null);
  const isFromSubscriptionRef = useRef(false);
  const saveTimeoutRef = useRef(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setAuthUserId(user?.uid ?? null);
    });
    return () => unsub();
  }, []);

  // Expose vehicles count to parent (e.g. for keyboard shortcuts next/prev vehicle)
  useEffect(() => {
    if (vehiclesCountRef) vehiclesCountRef.current = vehicles.length;
  }, [vehicles.length, vehiclesCountRef]);

  const refreshVehicles = useCallback(async () => {
    try {
      const loadedVehicles = await loadVehicles();
      isFromSubscriptionRef.current = true;
      setVehicles(loadedVehicles);
    } catch (err) {
      console.error('Error refreshing vehicles:', err);
    }
  }, []);

  const loadData = useCallback(async () => {
    setLoadError(null);
    setIsLoading(true);
    try {
      const loadedVehicles = await loadVehicles();
      setVehicles(loadedVehicles);

      const loadedInvoices = await getInvoices();
      setInvoices(loadedInvoices);

      const contact = await loadGarageContact();
      if (contact) {
        setGarageInfo(prev => ({
          ...prev,
          companyName: contact.companyName || prev.companyName,
          address: contact.address || prev.address,
          contact: contact.phone || prev.contact,
          vatNumber: contact.vatNumber || prev.vatNumber,
          email: contact.email || prev.email,
          siteNames: contact.siteNames || prev.siteNames,
          technicians: contact.technicians ?? prev.technicians ?? [],
          jobCardTemplate: contact.jobCardTemplate ?? prev.jobCardTemplate ?? { templateId: 'classic', customLabels: {} },
          defaultTechnician: contact.defaultTechnician ?? prev.defaultTechnician ?? '',
          defaultSite: contact.defaultSite ?? prev.defaultSite ?? '',
          branding: contact.branding ?? prev.branding ?? null,
          whatsappServiceDueRemindersEnabled: contact.whatsappServiceDueRemindersEnabled === true,
          whatsappMotRemindersEnabled: contact.whatsappMotRemindersEnabled === true,
          enabledStages: normalizeEnabledStages(contact.enabledStages),
        }));
        if (contact.branding && typeof contact.branding === 'object') {
          applyPlatformBranding(contact.branding);
        }
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setLoadError(err.message || 'Failed to load data. Check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load data on mount. Vehicles: real-time subscription so all users see each other's changes.
  // Invoices: real-time subscription.
  useEffect(() => {
    if (!shouldUseFirestore()) {
      loadData();
      return;
    }
    setLoadError(null);
    setIsLoading(true);
    const unsubV = subscribeVehicles(
      (data) => {
        isFromSubscriptionRef.current = true;
        setVehicles(data);
        setLoadError(null);
        setIsLoading(false);
      },
      (err) => {
        setLoadError(err?.message || 'Failed to load vehicles.');
        setIsLoading(false);
      }
    );
    const unsubI = subscribeInvoices(
      (data) => setInvoices(data),
      (err) => console.error('Invoices subscription error:', err)
    );
    const unsubGarage = subscribeGarageInfo(
      (contact) => {
        if (!contact) return;
        setGarageInfo((prev) => ({
          ...prev,
          companyName: contact.companyName || prev.companyName,
          address: contact.address || prev.address,
          contact: contact.phone || prev.contact,
          vatNumber: contact.vatNumber || prev.vatNumber,
          email: contact.email || prev.email,
          siteNames: contact.siteNames || prev.siteNames,
          technicians: contact.technicians ?? prev.technicians ?? [],
          jobCardTemplate: contact.jobCardTemplate ?? prev.jobCardTemplate ?? { templateId: 'classic', customLabels: {} },
          defaultTechnician: contact.defaultTechnician ?? prev.defaultTechnician ?? '',
          defaultSite: contact.defaultSite ?? prev.defaultSite ?? '',
          branding: contact.branding ?? prev.branding ?? null,
          whatsappServiceDueRemindersEnabled: contact.whatsappServiceDueRemindersEnabled === true,
          whatsappMotRemindersEnabled: contact.whatsappMotRemindersEnabled === true,
          enabledStages: normalizeEnabledStages(contact.enabledStages),
        }));
        if (contact.branding && typeof contact.branding === 'object') {
          applyPlatformBranding(contact.branding);
        }
      },
      (err) => console.error('Garage info subscription error:', err)
    );
    loadGarageContact().then((contact) => {
      if (contact) {
        setGarageInfo(prev => ({
          ...prev,
          companyName: contact.companyName || prev.companyName,
          address: contact.address || prev.address,
          contact: contact.phone || prev.contact,
          vatNumber: contact.vatNumber || prev.vatNumber,
          email: contact.email || prev.email,
          siteNames: contact.siteNames || prev.siteNames,
          technicians: contact.technicians ?? prev.technicians ?? [],
          jobCardTemplate: contact.jobCardTemplate ?? prev.jobCardTemplate ?? { templateId: 'classic', customLabels: {} },
          defaultTechnician: contact.defaultTechnician ?? prev.defaultTechnician ?? '',
          defaultSite: contact.defaultSite ?? prev.defaultSite ?? '',
          branding: contact.branding ?? prev.branding ?? null,
          whatsappServiceDueRemindersEnabled: contact.whatsappServiceDueRemindersEnabled === true,
          whatsappMotRemindersEnabled: contact.whatsappMotRemindersEnabled === true,
          enabledStages: normalizeEnabledStages(contact.enabledStages),
        }));
        if (contact.branding && typeof contact.branding === 'object') {
          applyPlatformBranding(contact.branding);
        }
      }
    }).catch((err) => {
      console.error('Error loading garage contact:', err);
    });
    return () => {
      unsubV();
      unsubI();
      unsubGarage();
    };
    // Re-subscribe when auth changes (e.g. user logs in) so real-time updates work
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUserId]);

  // Save vehicles to Firestore when user makes changes (not when we receive from subscription)
  // Debounced so rapid clicks/edits trigger one save after user stops
  useEffect(() => {
    if (isFromSubscriptionRef.current) {
      isFromSubscriptionRef.current = false;
      return;
    }
    if (!isLoading && vehicles.length > 0) {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = setTimeout(() => {
        saveTimeoutRef.current = null;
        saveVehicles(vehicles).catch(err => {
          console.error('Error saving vehicles:', err);
        });
      }, 300);
    }
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, [vehicles, isLoading]);

  const updateVehicle = useCallback((index, updates) => {
    setVehicles(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], ...updates };
      return updated;
    });
  }, []);

  const addVehicle = useCallback((vehicle) => {
    setVehicles(prev => [vehicle, ...prev]);
  }, []);

  const deleteVehicle = useCallback((index) => {
    setVehicles(prev => prev.filter((_, i) => i !== index));
  }, []);

  const addInvoice = useCallback(async (invoice) => {
    await saveInvoice(invoice);
    const updatedInvoices = await getInvoices();
    setInvoices(updatedInvoices);
  }, []);

  const createInvoiceForVehicle = useCallback(async (vehicle) => {
    try {
      const invoice = await createInvoiceForVehicleFunc(vehicle, generateInvoiceNumber);
      if (invoice) {
        console.log('Invoice created:', invoice);
        
        // Add invoice to state immediately so it appears in Invoice History right away
        setInvoices(prev => {
          // Check if invoice already exists to avoid duplicates
          const exists = prev.some(inv => inv.number === invoice.number);
          if (exists) {
            console.log('Invoice already in state, skipping duplicate');
            return prev;
          }
          // Add new invoice at the beginning (most recent first) and sort by date
          const updated = [invoice, ...prev];
          const sorted = updated.sort((a, b) => {
            const dateA = new Date(a.date);
            const dateB = new Date(b.date);
            return dateB - dateA; // Most recent first
          });
          console.log('Added invoice to state, total invoices:', sorted.length);
          console.log('Invoice details:', {
            number: invoice.number,
            date: invoice.date,
            reg: invoice.reg,
            total: invoice.total
          });
          return sorted;
        });
        
        // Don't refresh immediately - the invoice is already saved and added to state
        // The invoice will appear immediately in the UI
        // If needed, invoices will be refreshed when the page is reloaded or when explicitly requested
      } else {
        console.log('No invoice created (may already exist)');
      }
      return invoice;
    } catch (error) {
      console.error('Error in createInvoiceForVehicle:', error);
      throw error;
    }
  }, []);

  const updateGarageInfo = useCallback(async (info) => {
    setGarageInfo(prev => ({ ...prev, ...info }));
    if (info.branding != null) {
      const payload = normalizeBrandingPayload(info.branding);
      if (payload) {
        await garageInfoService.update({ branding: payload });
        applyPlatformBranding(payload);
      }
      return;
    }
    const hasChanges = info.companyName != null || info.address != null || info.contact != null ||
      info.email != null || info.vatNumber != null || info.siteNames != null || info.technicians != null ||
      info.jobCardTemplate != null || info.defaultTechnician != null || info.defaultSite != null ||
      typeof info.whatsappServiceDueRemindersEnabled === 'boolean' ||
      typeof info.whatsappMotRemindersEnabled === 'boolean' ||
      info.enabledStages != null;
    if (hasChanges) {
      const payload = {
        companyName: info.companyName ?? garageInfo.companyName ?? '',
        address: info.address ?? garageInfo.address,
        phone: info.contact ?? garageInfo.contact,
        email: info.email ?? garageInfo.email ?? '',
        vatNumber: info.vatNumber ?? garageInfo.vatNumber ?? '',
        siteNames: info.siteNames ?? garageInfo.siteNames ?? DEFAULT_GARAGE_INFO.siteNames,
        technicians: info.technicians ?? garageInfo.technicians ?? [],
        defaultTechnician: info.defaultTechnician ?? garageInfo.defaultTechnician ?? '',
        defaultSite: info.defaultSite ?? garageInfo.defaultSite ?? '',
        whatsappServiceDueRemindersEnabled:
          typeof info.whatsappServiceDueRemindersEnabled === 'boolean'
            ? info.whatsappServiceDueRemindersEnabled
            : garageInfo.whatsappServiceDueRemindersEnabled === true,
        whatsappMotRemindersEnabled:
          typeof info.whatsappMotRemindersEnabled === 'boolean'
            ? info.whatsappMotRemindersEnabled
            : garageInfo.whatsappMotRemindersEnabled === true,
        enabledStages: normalizeEnabledStages(info.enabledStages ?? garageInfo.enabledStages),
      };
      if (info.jobCardTemplate != null) {
        payload.jobCardTemplate = info.jobCardTemplate;
      } else if (garageInfo.jobCardTemplate != null) {
        payload.jobCardTemplate = garageInfo.jobCardTemplate;
      }
      await saveGarageContact(payload);
    }
  }, [garageInfo]);

  const value = useMemo(() => ({
    vehicles,
    setVehicles,
    refreshVehicles,
    refreshData: loadData,
    updateVehicle,
    addVehicle,
    deleteVehicle,
    invoices,
    setInvoices,
    addInvoice,
    createInvoiceForVehicle,
    getAllInvoices,
    generateInvoiceNumber,
    garageInfo,
    updateGarageInfo,
    isLoading,
    loadError,
  }), [vehicles, refreshVehicles, loadData, updateVehicle, addVehicle, deleteVehicle, invoices, addInvoice, createInvoiceForVehicle, garageInfo, updateGarageInfo, isLoading, loadError]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};


